import Column from "@/Components/Grid/Column";
import Container from "@/Components/Grid/Container";
import Row from "@/Components/Grid/Row";
import ContactForm from "@/Components/Universal/ContactForm";
import TopHero from "@/Components/Universal/TopHero";
import useIsServer from "@/Hooks/useIsServer";
import FrontLayout from "@/Layouts/FrontLayout";
import { PageProps } from "@/types";
import { PostModel } from "@/types/models/post";
import { Icon } from "@iconify/react";
import { Head, Link } from "@inertiajs/react";
import FsLightbox from "fslightbox-react";
import { ComponentType, Suspense, lazy, useEffect, useState } from "react";

interface ParsedJsonTextInterface {
    [key: string]: string;
}

const BOOKERO_POST_SLUGS = new Set([
    "zapraszamy-na-dzien-otwarty-w-sobote-24-maja",
    "zapraszamy-na-dzien-otwarty-w-piatek-24-pazdziernika",
    "zapraszamy-na-wieczor-otwarty-we-wtorek-22-lipca",
    "zapraszamy-na-dzien-otwarty-w-srode-28-stycznia",
    "zapraszamy-na-wieczor-otwarty-w-srode-11-marca",
    'zapraszamy-na-dzien-otwarty-w-piatek-19-czerwca',
    'zapraszamy-na-wieczor-otwarty'
]);

export default function Gallery({
    post,
}: PageProps<{
    post: PostModel;
}>) {
    const getFormat = (filename: string) => {
        return filename.split(".").pop() as string;
    };

    const [parsedText, setParsedText] = useState<ParsedJsonTextInterface>();
    const isServer = useIsServer();
    const showBookero = BOOKERO_POST_SLUGS.has(post.slug);
    const [lightboxController, setLightboxController] = useState({
        toggler: false,
        slide: 1,
    });

    const photos = parsedText
        ? Object.keys(parsedText)
            .filter((key) => key.includes("photo"))
            .map((key) => parsedText[key])
            .filter(Boolean)
        : [];

    const lightboxSources = photos.map((photo) => {
        const extension = getFormat(photo);
        return photo.replace(extension, "webp");
    });

    const openLightboxOnSlide = (number: number) => {
        setLightboxController({
            toggler: !lightboxController.toggler,
            slide: number,
        });
    };

    useEffect(() => {
        if (post.content != "") {
            setParsedText(JSON.parse(post.content));
        }
    }, []);

    const [videoId, setVideoId] = useState<string>("");

    useEffect(() => {
        const { video } = post;

        if (!video) {
            return;
        }

        const youtubeRegex =
            /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=|.*youtu\.be\/)|youtu\.be\/)([^"&?\/ ]{11})/;

        const match = video.match(youtubeRegex);

        if (match && match[1]) {
            const extractedVideoId = match[1];
            setVideoId(extractedVideoId);
        }
    }, []);

    const Loader = () => {
        return (
            <div className="w-10 h-10 border-[2px] bg-accordorange rounded-full animate-spin"></div>
        );
    };

    const IframeSection = ({ videoId }: { videoId: string }) => {
        const [Component, setComponent] = useState<ComponentType | null>(null);

        useEffect(() => {
            // @ts-ignore
            setComponent(() => lazy(() => import("./IframeSection")));
        }, []);

        if (!Component) {
            return <Loader />;
        }

        return (
            <Suspense fallback={<Loader />}>
                {/* @ts-ignore */}
                <Component videoId={videoId} />
            </Suspense>
        );
    };

    useEffect(() => {
        if (!showBookero) {
            return;
        }

        const container = document.getElementById("bookero-container");

        if (container) {
            // @ts-ignore
            if (window.bookero_config) {
                const script = document.createElement("script");
                script.src =
                    "https://cdn.bookero.pl/plugin/v2/js/bookero-compiled.js";
                script.async = true;
                document.body.appendChild(script);
            }
        }
    }, [showBookero]);

    return (
        <>
            <Head title="Osiedle Kusocińskiego">
                <meta name="description" content="Osiedle Kusocińskiego" />
            </Head>
            <FrontLayout>
                {!isServer && photos.length > 0 && (
                    <FsLightbox
                        toggler={lightboxController.toggler}
                        sources={lightboxSources}
                        slide={lightboxController.slide}
                    />
                )}
                <TopHero
                    image="general-breadcrumbs-bg.webp"
                    text={post.is_promotion ? "Promocje" : "Aktualności"}
                />
                <Container className="mt-20 mb-8 maxMd:my-8">
                    <Row>
                        <Column>
                            <div className="px-8">
                                <p className="uppercase text-label leading-label mb-2 text-secondary font-extrabold">
                                    Data dodania: {post.created_at}
                                </p>
                                <h2 className="text-h2 leading-h2 text-primary font-extrabold mb-8 maxMd:mb-6">
                                    {post.name}
                                </h2>
                                <div
                                    className="text-p text-text leading-p"
                                    dangerouslySetInnerHTML={{
                                        __html: post.excerpt,
                                    }}
                                ></div>
                            </div>
                        </Column>
                    </Row>
                </Container>
                {post.video && (
                    <Container>
                        <Row>
                            <Column>
                                <IframeSection videoId={videoId} />
                            </Column>
                        </Row>
                    </Container>
                )}
                <Container className="mt-20 mb-24 maxMd:my-14">
                    <Row>
                        <Column>
                            <div className="px-8 maxMd:px-0">
                                <div className="content-area">
                                    {parsedText != undefined &&
                                        Object.keys(parsedText).map((c) => {
                                            if (c.includes("content")) {
                                                return (
                                                    <div
                                                        key={c}
                                                        className="text-p leading-p text-text [&>p]:mb-6 maxMd:[&>p]:mb-4"
                                                        dangerouslySetInnerHTML={{
                                                            __html: parsedText[
                                                                c
                                                            ],
                                                        }}
                                                    ></div>
                                                );
                                            }
                                        })}

                                    {photos.length > 0 && (
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-8">
                                            {photos.map((photo, i) => (
                                                <div key={`${photo}-${i}`}>
                                                    <button
                                                        type="button"
                                                        className="w-full"
                                                        onClick={() =>
                                                            openLightboxOnSlide(
                                                                i + 1,
                                                            )
                                                        }
                                                    >
                                                        <picture>
                                                            <source
                                                                srcSet={`${photo.replace(/\.[^.]+$/, "")}.webp`}
                                                                type="image/webp"
                                                            />
                                                            <source
                                                                srcSet={`${photo.replace(/\.[^.]+$/, "")}.${getFormat(photo)}`}
                                                                type={`image/${getFormat(photo)}`}
                                                            />
                                                            <img
                                                                className="w-full aspect-square object-cover object-center"
                                                                src={photo}
                                                                alt={`${post.name} ${i + 1}`}
                                                            />
                                                        </picture>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Column>
                        {showBookero && (
                            <Column className="px-8 maxMd:px-4 py-10">
                                <div id="bookero-container"></div>
                            </Column>
                        )}
                        {post.slug ===
                            "zaprojektuj-murale-inwestycji-kusocinskiego-konkurs-dla-studentow-asp" && (
                                <Column className="px-8 maxMd:px-4 mb-20 maxMd:mb-10">
                                    <p className="text-h3 text-primary font-bold my-6">
                                        Materiały do pobrania:
                                    </p>
                                    <a
                                        download
                                        className="flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline"
                                        href="/konkurs/regulamin-konkursu-mural-kusocinskiego.pdf"
                                    >
                                        <Icon
                                            icon="mdi:file-outline"
                                            width="24"
                                            height="24"
                                        />
                                        Regulamin Konkursu Mural Kusocińskiego
                                    </a>
                                    <a
                                        download
                                        className="flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline"
                                        href="/konkurs/zalacznik-nr-1-wymiary-sciany-elewacyjnej-budynek-a-cad.dwg"
                                    >
                                        <Icon
                                            icon="mdi:file-outline"
                                            width="24"
                                            height="24"
                                        />
                                        Załącznik nr 1 - Wymiary ściany elewacyjnej
                                        - budynek A - cad
                                    </a>
                                    <a
                                        download
                                        className="flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline"
                                        href="/konkurs/zalacznik-nr-1-wymiary-sciany-elewacyjnej-budynek-a.pdf"
                                    >
                                        <Icon
                                            icon="mdi:file-outline"
                                            width="24"
                                            height="24"
                                        />
                                        Załącznik nr 1 - Wymiary Ściany Elewacyjnej
                                        - budynek A - pdf
                                    </a>
                                    <a
                                        download
                                        className="flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline"
                                        href="/konkurs/zalacznik-nr-2-wymiary-sciany-elewacyjnej-budynek-b-cad.dwg"
                                    >
                                        <Icon
                                            icon="mdi:file-outline"
                                            width="24"
                                            height="24"
                                        />
                                        Załącznik nr 2 - Wymiary ściany elewacyjnej
                                        - budynek B - cad
                                    </a>
                                    <a
                                        download
                                        className="flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline"
                                        href="/konkurs/zalacznik-nr-2-wymiary-sciany-elewacyjnej-budynek-b.pdf"
                                    >
                                        <Icon
                                            icon="mdi:file-outline"
                                            width="24"
                                            height="24"
                                        />
                                        Załącznik nr 2 - Wymiary ściany elewacyjnej
                                        - budynek B - pdf
                                    </a>
                                    <a
                                        download
                                        className="flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline"
                                        href="/konkurs/zalacznik-nr-3-brief-konkursowy.pdf"
                                    >
                                        <Icon
                                            icon="mdi:file-outline"
                                            width="24"
                                            height="24"
                                        />
                                        Załącznik nr 3 - Brief konkursowy
                                    </a>
                                    <a
                                        download
                                        className="flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline"
                                        href="/konkurs/zalacznik-nr-4-logotyp-inwestycji.zip"
                                    >
                                        <Icon
                                            icon="mdi:file-outline"
                                            width="24"
                                            height="24"
                                        />
                                        Załącznik nr 4 - Logotyp inwestycji
                                    </a>
                                    <a
                                        download
                                        className="flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline"
                                        href="/konkurs/zalacznik-nr-5-wizualizacje-inwestycji.zip"
                                    >
                                        <Icon
                                            icon="mdi:file-outline"
                                            width="24"
                                            height="24"
                                        />
                                        Załącznik nr 5 - Wizualizacje inwestycji
                                    </a>
                                    <a
                                        download
                                        className="flex gap-x-3 mb-5 text-primary items-center font-medium text-p underline"
                                        href="/konkurs/zalacznik-nr-6-oswiadczenie-o-nieposiadaniu-zarejestrowanej-dzialalnosci.odt"
                                    >
                                        <Icon
                                            icon="mdi:file-outline"
                                            width="24"
                                            height="24"
                                        />
                                        Załącznik nr 6 - Oświadczenie o
                                        nieposiadaniu zarejestrowanej działalności
                                    </a>
                                </Column>
                            )}
                        <Column>
                            <div className="px-8 maxMd:px-0">
                                <div className="content-area">
                                    <picture>
                                        <source
                                            srcSet={`${post.photo.replace(/\.[^.]+$/, "")}.webp`}
                                            type="image/webp"
                                        />
                                        <source
                                            srcSet={`${post.photo.replace(/\.[^.]+$/, "")}.${getFormat(post.photo)}`}
                                            type={`image/${getFormat(post.photo)}`}
                                        />
                                        <img
                                            className={`w-full max-h-[500px] maxMd:max-h-[220px] object-contain object-center mb-10 maxMd:mb-6`}
                                            src={post.photo}
                                            alt={post.name}
                                        />
                                    </picture>
                                </div>
                            </div>
                        </Column>

                        <Column>
                            <div className="px-8 maxMd:px-0">
                                <div className="content-area">
                                    <Link
                                        href={
                                            post.is_promotion
                                                ? route("frontend.promotions")
                                                : route("frontend.news")
                                        }
                                        className="button-outline max-w-[310px] ml-auto mt-2"
                                    >
                                        {post.is_promotion
                                            ? "Wróć do listy promocji"
                                            : "Wróc do listy wpisów"}
                                        <Icon
                                            icon={`streamline:return-2`}
                                            className="ml-1"
                                        />
                                    </Link>
                                </div>
                            </div>
                        </Column>
                    </Row>
                </Container>
                <ContactForm smallerMargin />
            </FrontLayout>
        </>
    );
}
